import type { AnexoChamado, InteracaoChamado } from "../../../types/portalCliente";

/**
 * Registros que o próprio sistema escreve na linha do tempo. Nos chamados do
 * cliente 9 eles são 4 de cada 5 interações públicas — só "O status do chamado
 * foi alterado para…" são 75% —, e com o mesmo peso visual de uma conversa
 * afogam o que alguém de fato escreveu.
 */
const PREFIXOS_DE_EVENTO =
  /^(o status do chamado|o chamado (foi|está|será)|chamado registrado|as informações do chamado|o anexo |a (solicitação|finalização) )/i;

/**
 * Texto longo nunca é registro automático: mesmo que comece parecido, é gente
 * escrevendo. Limite grande o bastante para caber "…Cancelado. Comentário: x".
 */
const LIMITE_DE_EVENTO = 300;

export const ehEvento = (interacao: InteracaoChamado): boolean => {
  const texto = interacao.comentario?.trim() ?? "";
  return texto.length > 0 && texto.length <= LIMITE_DE_EVENTO && PREFIXOS_DE_EVENTO.test(texto);
};

/**
 * Nome de quem escreveu. O autor chega como login de rede — "AVILASOLUCOES\\gustavo" —
 * e o domínio não diz nada a quem lê: quem é da T4i já vem marcado pelo crachá.
 * O login inteiro fica no title, para não perder o dado.
 */
export const nomeAutor = (interacao: InteracaoChamado): string => {
  // O apelido também vem com domínio nesses registros antigos, então o corte
  // vale para os dois campos, e não só para o autor.
  const bruto = interacao.apelido?.trim() || interacao.autor?.trim() || "";
  return bruto.replace(/^[^\\]*\\/, "").trim() || "—";
};

/** Iniciais para o avatar; duas no máximo. */
export const iniciais = (nome?: string | null): string => {
  const limpo = (nome ?? "").replace(/^[^\\]*\\/, "").trim();
  if (!limpo) return "?";
  const partes = limpo.split(/[\s._-]+/).filter(Boolean);
  const letras = partes.slice(0, 2).map((p) => p[0]);
  return letras.join("").toUpperCase() || "?";
};

/** Dia civil em ISO local, para agrupar sem passar por UTC. */
const diaISO = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

export const rotuloDoDia = (iso: string, agora = new Date()): string => {
  const dia = diaISO(iso);
  const hoje = diaISO(agora.toISOString());
  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);

  if (dia === hoje) return "Hoje";
  if (dia === diaISO(ontem.toISOString())) return "Ontem";

  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export interface DiaDaLinha {
  dia: string;
  rotulo: string;
  interacoes: InteracaoChamado[];
}

/** Agrupa em dias, preservando a ordem que veio do servidor. */
export const agruparPorDia = (interacoes: InteracaoChamado[]): DiaDaLinha[] => {
  const dias: DiaDaLinha[] = [];

  for (const interacao of interacoes) {
    const dia = diaISO(interacao.dataHora);
    const ultimo = dias[dias.length - 1];
    if (ultimo?.dia === dia) ultimo.interacoes.push(interacao);
    else dias.push({ dia, rotulo: rotuloDoDia(interacao.dataHora), interacoes: [interacao] });
  }

  return dias;
};

/** Anexos por interação: a lista vem solta, mas cada anexo sabe de onde veio. */
export const anexosPorInteracao = (
  anexos: AnexoChamado[]
): Map<number, AnexoChamado[]> => {
  const mapa = new Map<number, AnexoChamado[]>();
  for (const anexo of anexos) {
    if (anexo.idInteracaoChamado === null) continue;
    const atuais = mapa.get(anexo.idInteracaoChamado) ?? [];
    atuais.push(anexo);
    mapa.set(anexo.idInteracaoChamado, atuais);
  }
  return mapa;
};
